import React from 'react';
import { useMultiStep } from '../hooks/useMultiStep';
import { useFormSubmit } from '../hooks/useFormSubmit';
import { getImagesByStep } from '../utils/templateMapper';
import ZentorgaForm from '../components/ZentorgaForm';
import SurveyQuestion from '../components/SurveyQuestion';
import BenefitPeriod from '../../components/BenefitPeriod';
import KeepButton from '../components/KeepButton';
import SoundVideo from '../components/SoundVideo';
import '../styles/zentorga.scss';
import '../styles/template32.scss';

const Template32 = ({ landingData, urlCode, urlCodeSettingId }) => {
  const { currentStep, stepData, goToNextStep, updateStepData } = useMultiStep(3);
  const { handleSubmit } = useFormSubmit(urlCode, urlCodeSettingId);

  const stepImages = getImagesByStep(landingData, 3);

  const surveyData1 = {
    topic: landingData.surveyTitle1,
    answers: [
      landingData.surveyOpt1_1,
      landingData.surveyOpt1_2,
      landingData.surveyOpt1_3,
      landingData.surveyOpt1_4,
      landingData.surveyOpt1_5
    ].filter(Boolean)
  };

  const surveyData2 = {
    topic: landingData.surveyTitle2,
    answers: [
      landingData.surveyOpt2_1,
      landingData.surveyOpt2_2,
      landingData.surveyOpt2_3,
      landingData.surveyOpt2_4,
      landingData.surveyOpt2_5
    ].filter(Boolean)
  };

  const handleSurvey1Change = (value) => {
    updateStepData({ survey1: value });
  };

  const handleSurvey2Change = (value) => {
    updateStepData({ survey2: value });
  };

  const handleFormSubmit = async (formData) => {
    const submitData = {
      ...formData,
      selectedOption: stepData.survey1,
      selectedOption2: stepData.survey2
    };
    await handleSubmit(submitData);
  };

  const renderImages = (images) => {
    if (!images || images.length === 0) return null;
    return (
      <div className="zentorga-images">
        {images.map((img, idx) => {
          const fileExtension = img.url.split('.').pop().toLowerCase();
          const isVideo = ['mp4', 'webm', 'ogg', 'mov'].includes(fileExtension);
          return isVideo ? (
            <SoundVideo key={idx} src={img.url} loop={img.alt !== "false"} />
          ) : (
            <img key={idx} src={img.url} alt={img.alt} />
          );
        })}
      </div>
    );
  };

  return (
    <section className="template32-layout">
      {/* Step 1 */}
      {currentStep === 0 && (
        <div className="step step1-container">
          {renderImages(stepImages.step1)}

          <div className="surveySection">
            <SurveyQuestion
              topic={surveyData1.topic}
              answers={surveyData1.answers}
              onChange={handleSurvey1Change}
              questionNumber={1}
              displayType={landingData.surveyTopic?.topic?.[0]?.type || 'radio'}
            numberColor={landingData.surveyTopic?.numberColor}
            answerColor={landingData.surveyAnswer?.answerColor}
            />
          </div>

          <KeepButton
            onClick={() => goToNextStep()}
            imageUrl="https://d1k8m48tkgq306.cloudfront.net/images/eventiro/postBTN.png"
          />
        </div>
      )}

      {/* Step 2 */}
      {currentStep === 1 && (
        <div className="step step2-container">
          {renderImages(stepImages.step2)}

          <div className="surveySection">
            <SurveyQuestion
              topic={surveyData2.topic}
              answers={surveyData2.answers}
              onChange={handleSurvey2Change}
              questionNumber={2}
              displayType={landingData.surveyTopic?.topic?.[1]?.type || 'radio'}
            numberColor={landingData.surveyTopic?.numberColor}
            answerColor={landingData.surveyAnswer?.answerColor}
            />
          </div>

          <KeepButton
            onClick={() => goToNextStep()}
            imageUrl="https://d1k8m48tkgq306.cloudfront.net/images/eventiro/postBTN.png"
          />
        </div>
      )}

      {/* Step 3 */}
      {currentStep === 2 && (
        <div className="step step3-container">
          {renderImages(stepImages.step3)}

          {landingData.eventTarget && (
            <div className="eventTarget">
              <p>이벤트 대상자 : <span className="event-target-text">{landingData.eventTarget}</span></p>
            </div>
          )}

          {landingData.benefitDate && (
            <div className="benefitDate">
              <BenefitPeriod periodType={landingData.benefitDate} />
            </div>
          )}

          <ZentorgaForm
            onSubmit={handleFormSubmit}
            formStyle={landingData.formStyleClassName || 'gray'}
            consentStyle={landingData.privacyPolicyClassName || 'dark'}
            privacyPolicyName={landingData.privacyPolicyName || '본원'}
            privacyPolicy={landingData.privacyPolicy}
            marketingPolicy={landingData.marketingPolicy}
            buttonComponent={
              <button
                type="submit"
                form="zentorga-form"
                id="postBtn"
                className={`postBtn ${landingData.postBtnLocate === 'fixed' ? 'postBtn-fixed' : ''}`}
              >
                {landingData.postBtnUrl ? (
                  <img src={landingData.postBtnUrl} alt="신청하기" />
                ) : (
                  <span>신청하기</span>
                )}
              </button>
            }
          />

          {landingData.serviceInfoClassName && (
            <p
              className={`serviceInfoClassName ${landingData.serviceInfoClassName}`}
              id="serviceInfoClassName"
            >
              * 본 서비스는 전국 20여 개 이상의 제휴 병원에서 제공되며,<br />
              상담 신청 정보를 기반으로 전문 상담원이 적합한 제휴 병원을 안내해드립니다.
            </p>
          )}

          {landingData.footerClassName !== null && (landingData.footerClassName !== undefined || landingData.footerDetail) && (
            <footer className={`footerClassName ${landingData.footerClassName || ''}`}>
              {landingData.footerDetail && (
                <p className="footerDetail" style={{ whiteSpace: 'pre-line' }}>
                  {landingData.footerDetail}
                </p>
              )}
            </footer>
          )}
        </div>
      )}
    </section>
  );
};

export default Template32;
